import { Alert, Button, Text } from "@softmax/design-system";
import {
  breakpoints,
  colors,
  fonts,
  spacing,
} from "@softmax/design-system/tokens.stylex";
import * as stylex from "@stylexjs/stylex";
import type { CredentialListState } from "./credential-records";

export function CredentialListContent({
  state,
  summary,
  onRevoke,
  onRefresh,
}: {
  readonly state: CredentialListState;
  readonly summary: boolean;
  readonly onRevoke: (id: string) => void;
  readonly onRefresh: () => void;
}) {
  if (state.status === "loading")
    return (
      <output>
        <Text>Loading credentials…</Text>
      </output>
    );

  if (state.status === "error")
    return (
      <Alert tone="danger">
        {state.message}{" "}
        <Button size="sm" onClick={onRefresh}>
          Retry credentials
        </Button>
      </Alert>
    );

  const countLabel = state.rows.length === 1 ? "token" : "tokens";
  const list = (
    <ul {...stylex.props(styles.rows)}>
      {state.rows.map((row) => (
        <li key={row.id} {...stylex.props(styles.row)}>
          <div {...stylex.props(styles.details)}>
            <Text>
              <strong>{row.name}</strong>
            </Text>
            <Text as="p" size="small">
              <span {...stylex.props(styles.preview)}>{row.preview}</span> ·{" "}
              {row.access}
            </Text>
            <Text as="p" size="small" tone="muted">
              Created {row.created} · {row.expires}
            </Text>
          </div>
          <div>
            <Button
              size="sm"
              variant="danger"
              onClick={() => onRevoke(row.id)}
              aria-disabled={state.refreshing}
              aria-label={`Revoke ${row.name}`}
            >
              Revoke
            </Button>
          </div>
        </li>
      ))}
    </ul>
  );
  return (
    <>
      {state.refreshing ? (
        <output>
          <Text>Refreshing credentials…</Text>
        </output>
      ) : null}
      {state.refreshError ? (
        <Alert tone="danger">
          {state.refreshError}{" "}
          <Button size="sm" onClick={onRefresh}>
            Refresh credentials
          </Button>
        </Alert>
      ) : null}
      {summary && state.rows.length > 0 ? (
        <Text as="p">
          <strong>{state.rows.length}</strong> {countLabel}
        </Text>
      ) : null}
      {state.rows.length === 0 ? (
        <Text as="p" tone="muted">
          No credentials yet.
        </Text>
      ) : summary ? (
        <details>
          <summary>Show all {state.rows.length}</summary>
          {list}
        </details>
      ) : (
        list
      )}
    </>
  );
}

const styles = stylex.create({
  rows: {
    display: "grid",
    gap: spacing.space16,
    listStyleType: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "grid",
    gridTemplateColumns: { default: "1fr auto", [breakpoints.compact]: "1fr" },
    gap: spacing.space12,
    borderTopWidth: 1,
    borderTopStyle: "solid",
    borderTopColor: colors.border,
    paddingTop: spacing.space16,
    minWidth: 0,
  },
  details: { display: "grid", gap: spacing.space8, minWidth: 0 },
  preview: { fontFamily: fonts.mono, overflowWrap: "anywhere" },
});
